/**
 * O'ng tugma menyusi (Radix ContextMenu). Qator yoki kartani o'raydi. Elementlar holat va rolga qarab
 * features/<x>/menu.ts da quriladi (sof funksiya, test qilinadi) -- bu yerda faqat ko'rsatish.
 *
 * - Klaviatura: strelkalar, Enter, Esc; ichki menyu o'ng strelka bilan. Sensorli ekranda uzoq bosish.
 * - Brauzerning o'z menyusi: Shift + o'ng tugma, input/textarea ichida yoki matn belgilangan bo'lsa.
 * - Modal emas: ochiq menyu turganda boshqa qatorga o'ng tugma bossa, o'shaning menyusi ochiladi.
 * - Elementlar faqat menyu ochilganda quriladi (`items` funksiya bo'lsa).
 * - `DropdownMenu`: xuddi shu elementlar tugmadan ("⋯") ochiladi; portal'da chiziladi, jadval `overflow`i kesmaydi.
 */
import * as CM from "@radix-ui/react-context-menu";
import * as DM from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight, type LucideIcon } from "lucide-react";
import { useState, type ComponentType, type MouseEvent, type ReactElement, type ReactNode, type RefAttributes } from "react";
import { cn } from "@/lib/utils";

export interface MenuItem {
  type?: "item";
  key: string;
  label: string;
  icon?: LucideIcon;
  onSelect?: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** O'ng tomonda kichik matn (masalan joriy qiymat) */
  hint?: string;
  /** Joriy tanlov (masalan hozirgi status) -- belgi bilan ko'rsatiladi */
  checked?: boolean;
  /** Ikon o'rnida rangli nuqta (fon klassi, masalan STATUS_BG[status]) */
  dot?: string;
  /** Ichki menyu */
  items?: MenuEntry[];
}
export interface MenuSeparator {
  type: "separator";
}
export interface MenuLabel {
  type: "label";
  label: string;
}
export type MenuEntry = MenuItem | MenuSeparator | MenuLabel;
type Maybe<T> = T | false | null | undefined;

export const SEPARATOR: MenuSeparator = { type: "separator" };

/**
 * false/null'larni olib tashlaydi, bo'sh ichki menyuni yashiradi, ortiqcha ajratgichlarni
 * (boshida, oxirida, ketma-ket) va bo'sh bo'lim sarlavhalarini tozalaydi.
 */
export function compactMenu(entries: Maybe<MenuEntry>[]): MenuEntry[] {
  const kept: MenuEntry[] = [];
  for (const e of entries) {
    if (!e) continue;
    if (e.type !== "separator" && e.type !== "label" && e.items) {
      const items = compactMenu(e.items);
      if (items.length) kept.push({ ...e, items });
      continue;
    }
    kept.push(e);
  }
  // Sarlavha: keyingisi oddiy element bo'lmasa (bo'sh bo'lim) -- olib tashlanadi
  const noEmptyLabels = kept.filter((e, i) => {
    if (e.type !== "label") return true;
    const next = kept[i + 1];
    return !!next && next.type !== "separator" && next.type !== "label";
  });
  const out: MenuEntry[] = [];
  for (const e of noEmptyLabels) {
    if (e.type === "separator" && (out.length === 0 || out[out.length - 1].type === "separator")) continue;
    out.push(e);
  }
  while (out.length && out[out.length - 1].type === "separator") out.pop();
  return out;
}

function allowNativeMenu(e: MouseEvent<HTMLElement>): boolean {
  if (e.shiftKey) return true;
  const target = e.target as HTMLElement | null;
  if (target?.closest("input, textarea, select, [contenteditable='true']")) return true;
  const selection = window.getSelection();
  return !!selection && !selection.isCollapsed && selection.toString().trim() !== "" && e.currentTarget.contains(selection.anchorNode);
}

const CONTENT = "fade-in z-50 min-w-[220px] max-w-[320px] rounded-2xl border border-border/60 bg-surface p-1.5 text-[13px] text-ink shadow-menu outline-none";
const ITEM =
  "relative flex cursor-default items-center gap-2 rounded-lg px-2.5 py-1.5 outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-45 data-[highlighted]:bg-fill";

/** Ikkala Radix menyu bir xil primitivlarga ega: elementlar bitta renderer bilan chiziladi */
interface Prims {
  Item: ComponentType<CM.ContextMenuItemProps & RefAttributes<HTMLDivElement>>;
  Separator: ComponentType<CM.ContextMenuSeparatorProps & RefAttributes<HTMLDivElement>>;
  Label: ComponentType<CM.ContextMenuLabelProps & RefAttributes<HTMLDivElement>>;
  Sub: ComponentType<CM.ContextMenuSubProps>;
  SubTrigger: ComponentType<CM.ContextMenuSubTriggerProps & RefAttributes<HTMLDivElement>>;
  SubContent: ComponentType<CM.ContextMenuSubContentProps & RefAttributes<HTMLDivElement>>;
  Portal: ComponentType<{ children?: ReactNode }>;
}
const CM_PRIMS: Prims = { Item: CM.Item, Separator: CM.Separator, Label: CM.Label, Sub: CM.Sub, SubTrigger: CM.SubTrigger, SubContent: CM.SubContent, Portal: CM.Portal };
const DM_PRIMS: Prims = { Item: DM.Item, Separator: DM.Separator, Label: DM.Label, Sub: DM.Sub, SubTrigger: DM.SubTrigger, SubContent: DM.SubContent, Portal: DM.Portal };

export function ContextMenu({ items, children, disabled }: { items: MenuEntry[] | (() => MenuEntry[]); children: ReactElement; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const entries = open ? (typeof items === "function" ? items() : items) : [];
  return (
    <CM.Root onOpenChange={setOpen} modal={false}>
      <CM.Trigger
        asChild
        disabled={disabled}
        onContextMenuCapture={(e) => {
          // Brauzer menyusi kerak bo'lgan holatlarda Radix ishlovchisiga yetib bormaydi
          if (allowNativeMenu(e)) e.stopPropagation();
        }}
      >
        {children}
      </CM.Trigger>
      <CM.Portal>
        <CM.Content className={CONTENT} collisionPadding={8} loop>
          <Entries entries={entries} prims={CM_PRIMS} />
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  );
}

/**
 * Tugmadan ochiladigan menyu ("⋯"): o'sha obyektning o'ng tugma menyusi bilan bir xil elementlar (`features/<x>/menu.ts`).
 * Portal'da chiziladi -- jadval yoki karta `overflow`i kesmaydi, bitta qatorli ro'yxatda ham to'liq ko'rinadi.
 */
export function DropdownMenu({ items, children, align = "end" }: { items: MenuEntry[] | (() => MenuEntry[]); children: ReactElement; align?: "start" | "end" }) {
  const [open, setOpen] = useState(false);
  const entries = open ? (typeof items === "function" ? items() : items) : [];
  return (
    <DM.Root open={open} onOpenChange={setOpen} modal={false}>
      <DM.Trigger asChild>{children}</DM.Trigger>
      <DM.Portal>
        <DM.Content className={CONTENT} align={align} sideOffset={4} collisionPadding={8} loop>
          <Entries entries={entries} prims={DM_PRIMS} />
        </DM.Content>
      </DM.Portal>
    </DM.Root>
  );
}

function Entries({ entries, prims: P }: { entries: MenuEntry[]; prims: Prims }) {
  return (
    <>
      {entries.map((e, i) => {
        if (e.type === "separator") return <P.Separator key={`sep-${i}`} className="mx-1.5 my-1.5 h-px bg-line" />;
        if (e.type === "label") {
          return (
            <P.Label key={`label-${i}`} className="truncate px-2 pt-1.5 pb-1 text-[11px] font-semibold text-muted">
              {e.label}
            </P.Label>
          );
        }
        const Icon = e.icon;
        const body = (
          <>
            <span className="flex w-4 shrink-0 justify-center">
              {Icon ? (
                <Icon size={15} strokeWidth={1.75} aria-hidden />
              ) : e.dot ? (
                <span className={cn("h-2.5 w-2.5 rounded-full ring-1 ring-black/10 dark:ring-white/20", e.dot)} aria-hidden />
              ) : e.checked ? (
                <Check size={14} strokeWidth={2} aria-hidden />
              ) : null}
            </span>
            <span className="min-w-0 flex-1 truncate">{e.label}</span>
            {e.hint && <span className="ml-3 shrink-0 text-[11px] text-muted">{e.hint}</span>}
          </>
        );
        if (e.items) {
          return (
            <P.Sub key={e.key}>
              <P.SubTrigger disabled={e.disabled} className={cn(ITEM, "data-[state=open]:bg-fill")}>
                {body}
                <ChevronRight size={14} className="text-muted" aria-hidden />
              </P.SubTrigger>
              <P.Portal>
                <P.SubContent className={CONTENT} sideOffset={2} alignOffset={-5} collisionPadding={8} loop>
                  <Entries entries={e.items} prims={P} />
                </P.SubContent>
              </P.Portal>
            </P.Sub>
          );
        }
        return (
          <P.Item
            key={e.key}
            disabled={e.disabled}
            className={cn(ITEM, e.danger && "text-danger data-[highlighted]:bg-danger-soft", e.checked && "font-semibold")}
            onSelect={() => {
              // Menyu yopilgach bajariladi: undan ochilgan oyna (Dialog) fokusni to'g'ri oladi
              if (e.onSelect) window.setTimeout(e.onSelect, 0);
            }}
          >
            {body}
          </P.Item>
        );
      })}
    </>
  );
}
